import React, { useEffect, useState } from 'react'
import { ClipboardList, Plus, Copy, ExternalLink } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Badge, EmptyState, Field, Button, Alert, Modal, ModalHeader, Input, Select } from '../components/ui'
import { isAdmin } from '../auth'

const textareaStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 10, minHeight: 70, resize: 'vertical',
  border: '1px solid var(--border-strong)', fontSize: 13.5, color: 'var(--text)',
  background: 'var(--surface)', outline: 'none', fontFamily: 'inherit'
}

const jenisLabel = { GNG: 'Gangguan', PSB: 'Pasang Baru', CAB: 'Cabut', REL: 'Relokasi', UPG: 'Upgrade', PME: 'Pemeliharaan', PSG: 'Pasang Perangkat', OPT: 'Optik', SRV: 'Survey' }
const statusTone = { OPEN: 'warning', PROSES: 'brand' }

export default function ListPekerjaan() {
  // Halaman ini SHARED (admin & teknisi) -- teknisi cuma lihat daftar + salin link, tombol
  // Tambah Pekerjaan admin-only (backend-nya juga sudah digerbang requireAdmin).
  const admin = isAdmin()
  const [list, setList] = useState([])
  const [listToken, setListToken] = useState(null)
  const [loading, setLoading] = useState(true)
  const [jenisOptions, setJenisOptions] = useState([])
  const [copiedKey, setCopiedKey] = useState(null)

  const [showAdd, setShowAdd] = useState(false)
  const [newLokasi, setNewLokasi] = useState('')
  const [newJenis, setNewJenis] = useState('')
  const [newDetail, setNewDetail] = useState('')
  const [newTeknisi, setNewTeknisi] = useState('')
  const [addError, setAddError] = useState('')
  const [addSubmitting, setAddSubmitting] = useState(false)

  useEffect(() => { load(); if (admin) loadJenis() }, [])

  async function load() {
    setLoading(true)
    try {
      const res = await api.get('/pekerjaan/gabungan')
      setList(res.data.list)
      setListToken(res.data.listToken)
    } finally { setLoading(false) }
  }

  async function loadJenis() {
    const res = await api.get('/jenis-pekerjaan')
    setJenisOptions(res.data.filter(j => j.aktif && j.kode !== 'GNG'))
  }

  function openAddModal() {
    setNewLokasi(''); setNewJenis(jenisOptions[0]?.kode || ''); setNewDetail(''); setNewTeknisi(''); setAddError('')
    setShowAdd(true)
  }

  async function submitAdd() {
    setAddError('')
    if (!newLokasi.trim()) return setAddError('Lokasi/pelanggan wajib diisi')
    if (!newJenis) return setAddError('Pilih jenis pekerjaan')
    setAddSubmitting(true)
    try {
      await api.post('/pekerjaan', {
        lokasi: newLokasi.trim(), jenisPekerjaanKode: newJenis, detail: newDetail.trim(), teknisi: newTeknisi.trim()
      })
      setShowAdd(false)
      load()
    } catch (err) {
      setAddError(err.response?.data?.message || 'Gagal membuat pekerjaan')
    } finally { setAddSubmitting(false) }
  }

  function linkFor(item) {
    return item.tipe === 'GANGGUAN'
      ? `${window.location.origin}/tiket/${item.token}`
      : `${window.location.origin}/pekerjaan-list/${listToken}/${item.id}`
  }

  function copyLink(item) {
    navigator.clipboard.writeText(linkFor(item))
    setCopiedKey(`${item.tipe}-${item.id}`)
    setTimeout(() => setCopiedKey(null), 1500)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <PageHeader
          title="List Pekerjaan"
          subtitle="Gabungan semua pekerjaan yang masih berjalan — Tiket Gangguan + jenis lainnya (PSB, dll)"
        />
        {admin && <Button icon={Plus} onClick={openAddModal}>Tambah Pekerjaan</Button>}
      </div>

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} width={440}>
          <ModalHeader title="Tambah Pekerjaan" subtitle="Utk gangguan, gunakan Tiket Gangguan -> Tambah Tiket" onClose={() => setShowAdd(false)} />
          {addError && <Alert type="error">{addError}</Alert>}
          <Field label="Lokasi / Pelanggan" style={{ marginBottom: 14 }}>
            <Input value={newLokasi} onChange={e => setNewLokasi(e.target.value)} placeholder="mis. Jl. Merdeka No.12" />
          </Field>
          <Field label="Jenis Pekerjaan" style={{ marginBottom: 14 }}>
            <Select value={newJenis} onChange={e => setNewJenis(e.target.value)}>
              {jenisOptions.map(j => <option key={j.kode} value={j.kode}>{j.kode} — {j.nama}</option>)}
            </Select>
          </Field>
          <Field label="Detail (opsional)" style={{ marginBottom: 14 }}>
            <textarea style={textareaStyle} value={newDetail} onChange={e => setNewDetail(e.target.value)} placeholder="Mis. instalasi ODP ke rumah, kabel 30m" />
          </Field>
          <Field label="Teknisi (opsional, boleh dikosongkan)" style={{ marginBottom: 18 }}>
            <Input value={newTeknisi} onChange={e => setNewTeknisi(e.target.value)} placeholder="mis. budi, andi" />
          </Field>
          <Button loading={addSubmitting} onClick={submitAdd} style={{ width: '100%', padding: '12px' }}>
            {addSubmitting ? 'Menyimpan...' : 'Buat Pekerjaan'}
          </Button>
        </Modal>
      )}

      <Card style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#fafafc' }}>
              {['Dibuat', 'Lokasi', 'Jenis', 'Ringkas', 'Teknisi', 'Status', 'Link'].map(h => (
                <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}><EmptyState icon={ClipboardList} title="Memuat..." /></td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan={7}><EmptyState icon={ClipboardList} title="Tidak ada pekerjaan outstanding" /></td></tr>
            ) : list.map(item => (
              <tr key={`${item.tipe}-${item.id}`} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{item.dibuatAt}</td>
                <td style={{ padding: '10px 16px', fontWeight: 600 }}>{item.lokasi}</td>
                <td style={{ padding: '10px 16px' }}><Badge tone="neutral">{jenisLabel[item.jenis] || item.jenis}</Badge></td>
                <td style={{ padding: '10px 16px', maxWidth: 260 }}>
                  {item.ringkas}
                  {item.catatanTerakhir && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>Catatan: {item.catatanTerakhir}</div>}
                </td>
                <td style={{ padding: '10px 16px' }}>{item.teknisi || '-'}</td>
                <td style={{ padding: '10px 16px' }}><Badge tone={statusTone[item.status] || 'neutral'}>{item.status}</Badge></td>
                <td style={{ padding: '10px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => copyLink(item)} title="Salin link" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                      <Copy size={15} />
                    </button>
                    <a href={linkFor(item)} target="_blank" rel="noreferrer" title="Buka" style={{ color: 'var(--text-muted)' }}>
                      <ExternalLink size={15} />
                    </a>
                    {copiedKey === `${item.tipe}-${item.id}` && <span style={{ fontSize: 11, color: 'var(--success)' }}>Tersalin!</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
